import roadmapThumbnailSrc from '@assets/images/roadmap.png';
import { Button, Container, Image, Stack } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Margin, usePDF } from 'react-to-pdf';

import { RoadmapNode } from '@/api/rest/resources/types/organization';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { usePathSet } from '@/hooks/usePathSet';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { assertIsDefined } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';

import { ComingSoon } from '../../components/ComingSoon';
import { useIsLimitedAccessUser } from '../../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../../hooks/useIsOrganizationMember';
import { useOrganizationId } from '../../hooks/useOrganizationId';
import { useOrganizationRoadmapNodes } from '../../hooks/useOrganizationRoadmapNodes';
import { DeleteRoadmapNodeWithConfirmation } from '../components/DeleteRoadmapNodeWithConfirmation';
import { AddRoadmap, EditRoadmapNode } from '../components/EditRoadmapNode';
import { Node } from '../components/RoadmapNode';

export const RoadmapPage = () => {
  const { t } = useTranslation();
  const options = {
    filename: `${t('navbar.goals')}_${new Date().toISOString()}.pdf`,
    page: {
      margin: Margin.MEDIUM,
    },
  };
  const { toPDF, targetRef } = usePDF(options);

  const navigate = useNavigate();
  const pathSet = usePathSet();

  const [roadmapForEdit, setRoadmapForEdit] = useState<RoadmapNode | null>(null);
  const [roadmapForDelete, setRoadmapForDelete] = useState<RoadmapNode | null>(null);

  const organizationId = useOrganizationId();
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const hideRoadmap = isLimitedAccessUser || !isOrganizationMember;

  const { roadmapId } = useParams();
  assertIsDefined(roadmapId, 'roadmapId');
  const roadmap = useOrganizationRoadmapNodes(undefined, { roadmap_node_id: roadmapId }).data.at(0);
  if (isNullish(roadmap)) {
    throw new Error(`Roadmap ${roadmapId} not found`);
  }

  const isStaffUser = useIsStaffUser();

  return (
    <SignedInPageLayout
      items={[]}
      navBarProps={{
        title: roadmap.title,
        showFilters: false,
        children: !hideRoadmap ? (
          <Stack direction='row' className='justify-end gap-4'>
            {isStaffUser && (
              <Button
                data-testid='delete-roadmap-button'
                onClick={() => setRoadmapForDelete(roadmap)}
                variant='outline'
                leftAdornment={<Icon icon='ri-delete-bin-line' />}
                className='px-3 py-3 rounded-3xl border border-neutral-black-12'
              />
            )}
            {isStaffUser && (
              <Button
                data-testid='edit-roadmap-button'
                onClick={() => setRoadmapForEdit(roadmap)}
                variant='outline'
                leftAdornment={<Icon icon='ri-pencil-line' />}
                className='px-3 py-3 rounded-3xl border border-neutral-black-12'
              />
            )}
            <Button type='button' variant='outline' onClick={() => toPDF()}>
              {t('navbar.actionButtons.dashboard')}
            </Button>
          </Stack>
        ) : null,
      }}
    >
      <Container ref={targetRef} gutterWidth={{ sm: 2 }} className='gap-10 my-8 ' data-testid='roadmap-page'>
        <Stack spacing={10}>
          {hideRoadmap ? (
            <ComingSoon />
          ) : (
            <>
              {!isNullish(roadmapForEdit) && (
                <EditRoadmapNode
                  organizationId={organizationId}
                  roadmapNode={roadmapForEdit}
                  onSuccess={() => setRoadmapForEdit(null)}
                  onClose={() => setRoadmapForEdit(null)}
                />
              )}
              <DeleteRoadmapNodeWithConfirmation
                roadmapNode={roadmapForDelete}
                onSuccess={() => navigate(pathSet.goals)}
                onClose={() => {
                  setRoadmapForDelete(null);
                }}
              />
              <Roadmap roadmap={roadmap} />
            </>
          )}
        </Stack>
      </Container>
    </SignedInPageLayout>
  );
};

const Roadmap = ({ roadmap }: { roadmap: RoadmapNode }) => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const isStaffUser = useIsStaffUser();

  if (!isStaffUser && !roadmap.visible_to_customer) return null;

  return (
    <Stack className='gap-6 group/roadmap'>
      <Stack className='grid grid-cols-1' data-testid='roadmap-header'>
        <Image
          alt='roadmap-thumbnail'
          fallback={roadmapThumbnailSrc}
          src={roadmapThumbnailSrc}
          className='col-start-1 row-start-1 w-full bg-secondary-30 object-cover h-96 rounded-2xl'
        />
        <Stack className='p-6 col-start-1 row-start-1 w-full bg-backdrop-overlay h-96 rounded-2xl gap-2 text-white-100 items-center justify-center'>
          <span className='typography-h3 whitespace-normal'>{roadmap.title}</span>
          <span className='typography-body1 whitespace-normal'>{roadmap.description}</span>
        </Stack>
      </Stack>
      <Stack data-testid='roadmap-content' className='gap-0.5 p-6 border border-neutral-black-12 rounded-2xl'>
        {roadmap.children?.map(
          (child) => !isNullish(child.id) && <Node key={child.id} roadmapId={child.id} showTimeline={true} />,
        )}
        {isStaffUser && (
          <AddRoadmap
            organizationId={organizationId}
            title={t('shared.goals.editNode.addMilestone')}
            parentId={roadmap.id}
            className='self-center'
          />
        )}
      </Stack>
    </Stack>
  );
};
